import clsx, { type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export const cn = (...inputs: ClassValue[]): string => twMerge(clsx(inputs));

export const getErrorMessage = (
  error: unknown,
  fallback = "Something went wrong",
): string => {
  if (!error) {
    return fallback;
  }

  if (typeof error === "string") {
    return error;
  }

  if (error instanceof Error) {
    const apiLikeError = error as Error & {
      data?: unknown;
      details?: unknown;
    };
    const details = apiLikeError.details;
    if (Array.isArray(details) && details.length > 0) {
      const first = details[0] as unknown;
      if (
        first &&
        typeof first === "object" &&
        "message" in first &&
        typeof (first as { message?: unknown }).message === "string"
      ) {
        const detailMessage = (first as { message: string }).message.trim();
        const genericMessage = error.message?.trim();
        if (
          detailMessage &&
          (!genericMessage ||
            genericMessage === "Validation failed" ||
            genericMessage === "Invalid request")
        ) {
          return detailMessage;
        }
      }
    }
    if (error.message) {
      return error.message;
    }
    if (
      apiLikeError.data &&
      typeof apiLikeError.data === "object" &&
      "message" in apiLikeError.data &&
      typeof (apiLikeError.data as { message?: unknown }).message === "string"
    ) {
      return (apiLikeError.data as { message: string }).message;
    }
    return error.message || fallback;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }

  return fallback;
};

/**
 * Format role names for display in the UI
 * Converts backend role names to user-friendly labels
 *
 * Supported roles: end_user, admin, owner, and member (UI default for public cards).
 */
export const formatRoleLabel = (role: string | null | undefined): string => {
  if (!role) {
    return "Unknown";
  }

  const normalizedRole = String(role).toLowerCase().trim();

  const roleMap: Record<string, string> = {
    end_user: "Viewer",
    admin: "Admin",
    owner: "Owner",
    member: "Member",
  };

  return (
    roleMap[normalizedRole] ||
    normalizedRole.charAt(0).toUpperCase() + normalizedRole.slice(1)
  );
};

const IMAGE_MARKDOWN_REGEX = /!\[(?:[^\]]*)\]\(([^)]+)\)/g;

// Broader regex to catch image-like URLs even without standard extensions (e.g. Unsplash)
// Matches http/https URLs that might be images.
const IMAGE_URL_REGEX =
  /(https?:\/\/[^\s)]+?(?:\.(?:png|jpe?g|gif|webp)|images\.unsplash\.com\/photo-[^\s)]+)(?:\?[^\s)]*)?)/gi;

const IMAGE_URL_LINE_REGEX =
  /^\s*(https?:\/\/[^\s)]+?(?:\.(?:png|jpe?g|gif|webp)|images\.unsplash\.com\/photo-[^\s)]+)(?:\?[^\s)]*)?)\s*$/gim;

const normalizeMarkdownImageUrls = (value: string): string =>
  value.replace(
    /(!\[(?:[^\]]*)\]\()([^)]+)(\))/g,
    (match, prefix, content, suffix) => {
      if (typeof content === "string" && /[\r\n]/.test(content)) {
        return `${prefix}${content.replace(/[\r\n]+/g, "")}${suffix}`;
      }
      return match;
    },
  );

export function extractImageUrlsFromContent(raw: string): string[] {
  if (!raw || typeof raw !== "string") {
    return [];
  }

  const normalized = normalizeMarkdownImageUrls(raw);
  const urls = new Set<string>();

  IMAGE_MARKDOWN_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null = IMAGE_MARKDOWN_REGEX.exec(normalized);
  while (match !== null) {
    const candidateGroup = match[1]?.trim();
    if (candidateGroup) {
      // Split by space to separate URL from optional title (e.g. "https://u.rl" "Title")
      // We take the first part which is the URL
      const urlCandidate = candidateGroup.split(/\s+/)[0];
      if (urlCandidate) {
        urls.add(urlCandidate);
      }
    }
    match = IMAGE_MARKDOWN_REGEX.exec(normalized);
  }

  IMAGE_URL_REGEX.lastIndex = 0;
  match = IMAGE_URL_REGEX.exec(normalized);
  while (match !== null) {
    const candidate = match[1]?.trim();
    if (candidate) {
      urls.add(candidate);
    }
    match = IMAGE_URL_REGEX.exec(normalized);
  }

  return Array.from(urls);
}

export function stripImageReferences(value?: string): string {
  if (!value || typeof value !== "string") {
    return "";
  }
  IMAGE_MARKDOWN_REGEX.lastIndex = 0;
  IMAGE_URL_LINE_REGEX.lastIndex = 0;
  return value
    .replace(IMAGE_MARKDOWN_REGEX, "")
    .replace(IMAGE_URL_LINE_REGEX, "");
}
